/**
 * MAYOR'S DESK — the one-click morning brief.
 *
 * One screen a mayor can read in thirty seconds and forward to his group: the
 * single thing that needs a decision, the situations behind it, the ranked next
 * steps, and the morning's headlines with plain-language versions of anything
 * that arrived in another language.
 *
 * The layout is a BENTO, not a card grid: compartments differ in size because
 * their contents differ. The lead earns two rows and seven columns because it
 * carries a headline, its evidence and a next step; the morning headlines earn
 * eight columns because each entry shows a plain line AND the original line.
 * The geometry is decided in `lib/mayorBrief.ts` (`placeBriefAreas`), so it can
 * be pinned by a unit test, and it leaves no hole in any feed mix.
 *
 * On translation: the model TRANSLATES and nothing else. The original headline
 * is always on screen next to the plain one, with its source and link. A
 * missing translation is a rendered state, never an error, and the brief opens
 * with untranslated headlines while the model answers.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { detectLang, needsTranslation, type IntelligenceItem } from "@yala/shared";
import { API_BASE } from "../lib/apiBase";
import { PanelHeader } from "./PanelHeader";
import { useLang } from "../hooks/useLang";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { ago } from "../lib/time";
import {
  BRIEF_NEWS_LIMIT,
  buildMayorBrief,
  briefLevelLabel,
  briefText,
  levelFromSeverity,
  type BriefArea,
  type BriefLevel,
  type BriefNewsItem,
  type BriefSignal,
} from "../lib/mayorBrief";
import type { ActionItem } from "../lib/actionBoard";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Ranked decisions from the action board — the lead comes from here. */
  actions: ActionItem[];
  /** The five situation compartments. */
  signals: BriefSignal[];
  /** The morning's headlines. */
  news: IntelligenceItem[];
  /** True while the action board is still evaluating — the brief says so. */
  loading?: boolean;
}

/** Severity → the house colour ramp. Same vocabulary as the action board. */
const LEVEL_COLOR: Record<BriefLevel, string> = {
  critical: "var(--bad)",
  warning: "var(--warn)",
  watch: "var(--data)",
  good: "var(--good)",
  unknown: "var(--ink-low)",
};

type CopyState = "idle" | "copied" | "failed";
type TranslationState = "idle" | "loading" | "done" | "unavailable";

/** Grid placement for a compartment, as CSS grid line numbers. */
function areaStyle(area: BriefArea | undefined): React.CSSProperties {
  if (!area) return {};
  return {
    gridColumn: `${area.col} / span ${area.span}`,
    gridRow: `${area.row} / span ${area.rows}`,
  };
}

/**
 * The provenance of the news compartment.
 *
 * It must say what is actually on screen: all machine-translated, none
 * translated, or a mix of the two. A panel that says "machine-translated"
 * while half its headlines are untouched originals is a small lie.
 */
export function newsSourceLabel(items: BriefNewsItem[]): string {
  const translated = items.filter((n) => n.plain !== null).length;
  if (items.length === 0) return "no headlines";
  if (translated === 0) return "original language";
  if (translated === items.length) return "machine-translated";
  return `${translated}/${items.length} machine-translated`;
}

export function MayorsDesk({ open, onClose, actions, signals, news, loading }: Props) {
  const { lang } = useLang();
  const th = lang === "th";
  const containerRef = useFocusTrap(open);
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [translationState, setTranslationState] = useState<TranslationState>("idle");
  const [translationNote, setTranslationNote] = useState<string | null>(null);
  const [rejectedCount, setRejectedCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (copyState !== "copied") return;
    const timer = window.setTimeout(() => setCopyState("idle"), 2500);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  // The headline set is the translation request's identity: when the feed
  // changes, the request changes. Keyed on the ids so a re-render with the
  // same headlines does not re-ask the model.
  //
  // Only the headlines the brief actually SHOWS are asked for. Translating
  // twelve to display four would spend the quota on text nobody reads.
  const shownNews = useMemo(() => news.slice(0, BRIEF_NEWS_LIMIT), [news]);
  const newsKey = useMemo(() => shownNews.map((n) => n.id).join("|"), [shownNews]);

  useEffect(() => {
    if (!open) return;
    const needing = shownNews.filter((n) => needsTranslation(n.title, lang));
    if (needing.length === 0) {
      setTranslations({});
      setTranslationState("done");
      setTranslationNote(null);
      setRejectedCount(0);
      return;
    }

    let cancelled = false;
    setTranslationState("loading");
    setTranslationNote(null);

    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/brief/plain`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            targetLang: lang,
            items: needing.map((n) => ({
              id: n.id,
              title: n.title,
              summary: n.summary,
              lang: n.lang ?? detectLang(n.title),
            })),
          }),
        });
        if (!res.ok) throw new Error(`http ${res.status}`);
        const json = (await res.json()) as {
          items?: Array<{ id?: string; plain?: string | null }>;
          meta?: { note?: string; rejected?: number };
        };
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const it of json.items ?? []) {
          if (it?.id && typeof it.plain === "string" && it.plain.trim()) map[it.id] = it.plain.trim();
        }
        setTranslations(map);
        setTranslationState(Object.keys(map).length > 0 ? "done" : "unavailable");
        setTranslationNote(json.meta?.note ?? null);
        setRejectedCount(typeof json.meta?.rejected === "number" ? json.meta.rejected : 0);
      } catch {
        if (cancelled) return;
        // A translation that never arrives is a state, not a failure: the
        // original headlines are already on screen.
        setTranslationState("unavailable");
        setTranslationNote(null);
        setRejectedCount(0);
      }
    })();

    return () => { cancelled = true; };
    // newsKey stands in for news: the request is about WHICH headlines, not
    // about the array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lang, newsKey]);

  const brief = useMemo(() => {
    const items: BriefNewsItem[] = news.map((n) => ({
      id: n.id,
      title: n.title,
      lang: n.lang ?? detectLang(n.title),
      plain: null,
      source: n.source,
      sourceUrl: n.sourceUrl,
      publishedAt: n.publishedAt,
      tags: n.tags,
    }));
    return buildMayorBrief({ actions, signals, news: items, translations });
  }, [actions, signals, news, translations]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(briefText(brief, lang, new Date()));
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  };

  if (!open) return null;

  const pick = (b: { en: string; th: string }) => (th ? b.th : b.en);
  const leadLevel = brief.lead ? levelFromSeverity(brief.lead.severity) : "unknown";

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        ref={containerRef}
        className="modal modal--mayor"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={th ? "โต๊ะนายกเทศมนครยะลา" : "Mayor's desk — Yala"}
      >
        <header className="modal-head modal-head--mayor">
          <div className="col">
            <span className="eyebrow mono">{th ? "โต๊ะนายกเทศมนคร // ยะลา" : "MAYOR'S DESK // YALA"}</span>
            <h2>{th ? "สรุปเช้าวันนี้" : "This morning, in one screen"}</h2>
          </div>
          <div className="mayor-actions">
            <button type="button" onClick={copy} className="mayor-btn mayor-btn--primary">
              {copyState === "copied"
                ? (th ? "คัดลอกแล้ว" : "Copied")
                : copyState === "failed"
                  ? (th ? "คัดลอกไม่สำเร็จ" : "Copy failed")
                  : (th ? "คัดลอกส่งไลน์" : "Copy for LINE")}
            </button>
            <button type="button" onClick={() => window.print()} className="mayor-btn">
              {th ? "พิมพ์" : "Print"}
            </button>
            <button type="button" onClick={onClose} className="mayor-btn mayor-btn--close">
              [ESC] CLOSE
            </button>
          </div>
        </header>

        <div className="modal-summary mono">
          <span className="mayor-summary">{pick(brief.summary)}</span>
          <span className="mayor-summary-level" style={{ color: LEVEL_COLOR[brief.level] }}>
            {pick(briefLevelLabel(brief.level))}
          </span>
        </div>

        <div className="modal-body mayor-body">
          {loading ? (
            <p className="mayor-note mono">{th ? "กำลังประเมินสถานการณ์…" : "evaluating…"}</p>
          ) : null}

          <div className="mayor-grid">
            {/* ── LEAD — the one thing that needs a decision ───────────────── */}
            <section className="mayor-panel mayor-panel--lead" style={areaStyle(brief.areas.lead)}>
              <PanelHeader
                title={th ? "เรื่องที่ต้องตัดสินใจ" : "WHAT NEEDS A DECISION"}
                source="action board"
                actions={
                  <span className="mono mayor-level" style={{ color: LEVEL_COLOR[leadLevel] }}>
                    {pick(briefLevelLabel(leadLevel))}
                  </span>
                }
              />
              {brief.lead ? (
                <>
                  <p className="mayor-lead-title">{pick(brief.lead.title)}</p>
                  <p className="mayor-lead-evidence mono">{brief.lead.evidence}</p>
                  <p className="mayor-lead-action">
                    <span className="eyebrow mono">{th ? "ขั้นตอนถัดไป" : "NEXT STEP"}</span>
                    {pick(brief.lead.action)}
                  </p>
                </>
              ) : (
                <p className="mayor-lead-title">
                  {th
                    ? "ไม่มีรายการเกินเกณฑ์ — สัญญาณที่ติดตามอยู่ส่งข้อมูลครบ"
                    : "Nothing is over threshold — all monitored signals are reporting"}
                </p>
              )}
            </section>

            {/* ── SITUATIONS — ordered by severity, so the worst is biggest ─── */}
            {brief.signals.map((s) => (
              <section
                key={s.key}
                className="mayor-panel mayor-panel--signal"
                style={areaStyle(brief.areas[s.key])}
              >
                <PanelHeader
                  title={pick(s.title)}
                  source={s.source}
                  ageMinutes={s.ageMinutes}
                  actions={
                    <span className="mono mayor-level" style={{ color: LEVEL_COLOR[s.level] }}>
                      {pick(briefLevelLabel(s.level))}
                    </span>
                  }
                />
                <p className="mayor-signal-value">{pick(s.value)}</p>
                {s.facts ? <p className="mayor-signal-facts mono">{pick(s.facts)}</p> : null}
              </section>
            ))}

            {/* ── RANKED STEPS ─────────────────────────────────────────────── */}
            <section className="mayor-panel mayor-panel--actions" style={areaStyle(brief.areas.actions)}>
              <PanelHeader
                title={th ? "ลำดับสิ่งที่ต้องทำ" : "RANKED STEPS"}
                source="rules over live feeds"
              />
              {brief.actions.length === 0 ? (
                <p className="mayor-empty">
                  {th ? "ไม่มีรายการที่ต้องตัดสินใจ" : "Nothing to decide right now"}
                </p>
              ) : (
                <ol className="mayor-steps">
                  {brief.actions.map((a) => {
                    const level = levelFromSeverity(a.severity);
                    return (
                      <li key={a.id} className="mayor-step">
                        <span className="mono mayor-step-level" style={{ color: LEVEL_COLOR[level] }}>
                          {pick(briefLevelLabel(level))}
                        </span>
                        <span className="mayor-step-body">
                          <strong>{pick(a.title)}</strong>
                          <span className="mayor-step-action">{pick(a.action)}</span>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>

            {/* ── MORNING NEWS — plain language beside the original ────────── */}
            <section className="mayor-panel mayor-panel--news" style={areaStyle(brief.areas.news)}>
              <PanelHeader
                title={th ? "ข่าวเช้า" : "MORNING NEWS"}
                source={newsSourceLabel(brief.news)}
                actions={
                  translationState === "loading" ? (
                    <span className="mono mayor-level">{th ? "กำลังแปล…" : "translating…"}</span>
                  ) : null
                }
              />
              {brief.news.length === 0 ? (
                <p className="mayor-empty">{th ? "ยังไม่มีข่าว" : "No headlines yet"}</p>
              ) : (
                <ul className="mayor-news">
                  {brief.news.map((n) => (
                    <li key={n.id} className="mayor-news-item">
                      {n.plain ? (
                        <>
                          <p className="mayor-news-plain">
                            {n.plain}
                            <span
                              className="mono mayor-ai-badge"
                              title="Machine-translated by a language model. The original headline is below."
                            >
                              AI
                            </span>
                          </p>
                          <p className="mayor-news-original">{n.title}</p>
                        </>
                      ) : (
                        <p className="mayor-news-original mayor-news-original--only">{n.title}</p>
                      )}
                      <p className="mayor-news-meta mono">
                        {n.source}
                        {n.publishedAt ? ` · ${ago(n.publishedAt)}` : ""}
                        {n.sourceUrl ? (
                          <>
                            {" · "}
                            <a href={n.sourceUrl} target="_blank" rel="noreferrer">
                              {th ? "เปิดข่าว" : "open"}
                            </a>
                          </>
                        ) : null}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {rejectedCount > 0 ? (
                <p className="mayor-note mono">
                  {th
                    ? `${rejectedCount} พาดหัวข่าวแสดงตามภาษาต้นฉบับ — ไม่ผ่านการตรวจสอบความถูกต้องกับต้นฉบับ`
                    : `${rejectedCount} headline${rejectedCount === 1 ? "" : "s"} shown in the original language — it did not pass the source-fidelity check`}
                </p>
              ) : translationState === "unavailable" ? (
                <p className="mayor-note mono">
                  {translationNote
                    ? translationNote
                    : th
                      ? "แสดงพาดหัวข่าวตามภาษาต้นฉบับ — ไม่มีการแปล"
                      : "Headlines are shown in their original language — no translation available"}
                </p>
              ) : null}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
